import { makeAutoObservable } from "mobx";
import { logError } from "@/utils/log";
import { AxiosInstance, AxiosResponse } from "axios";

import {
  ExtendedShareholderDTO,
  NewShareholderResponse,
  PersonInfo,
  ShareholderDetailsResponseDTO,
  ShareholderField,
  ShareholdersInfo,
  ShareholderType,
  totalNumberOfSharesOfShareholder,
  totalNumberOfVotesForShareholder,
  UpdateShareholderFieldResponse,
} from "@/types/Shareholder";
import { Transaction } from "@/types/Transactions";
import { SuggestedCompany } from "@/types/CompanyInfo";
import { DownloadService } from "@/api/DownloadService";
import {
  ApiService,
  downloadFile,
  getResponseFilename,
  UploadShareholdersExcelResponse,
} from "@/api";
import { isNotBlank, removeExtraSpaces } from "@/utils/strings";
import AlertMessageStore from "./AlertMessageStore";
import { formatPhoneNumber } from "@/utils/phoneNumbers";
import {
  importShareholdersErrorMessage,
  ParseExcelResponse,
  ShareholderIdShares,
  uploadShareholdersErrorMessage,
} from "@/types/excelImportDomain";
import { compact, orderBy } from "lodash";
import { failure, isPending, ok, pending, Result } from "@/common/common";
import { ownershipApi, OwnershipGraphType } from "@/overview/ownership/commonOwnershipDomain";
import type { OwnershipGraph } from "@/overview/ownership/ownershipDomain";
import { atTime, diffDays, toDate } from "@/utils/Dates";
import UserStore from "./UserStore";
import { History } from "history";
import {
  ShareholderModel,
  validateAllShareholders,
} from "@/shareholderRegister/shareholders/ShareholderModel";

import { lookupNorwegianCity } from "@/utils/postalCodes";
import { navigateToNewTransaction, navigateToShareholdersList } from "@/utils/navigation";
import { isCountryCodeNorway, isCountryCodeSweden, norway } from "@/utils/countries";
import { DialogStore } from "@/dialogs/dialogDomain";
import { TransactionType } from "@/types/enums/TransactionType";

import { CURRENCY_NOK } from "@/utils/currency";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { Language } from "@/i18n/languages";
import { TFunction, TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { ShareholdingPerDateModel } from "@/shareholderRegister/transactions/shareholdingPerDate/shareholdingPerDateDomain";
import { when } from "@/utils/functions";
import { isSwedishOrgNo } from "@/utils/orgNo";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import {
  getShareSumByType,
  getShareSumWithoutCompany,
  ShareholderPledgeForm,
  ShareholderRemarksForm,
  ShareholderStatus,
} from "@/shareholderRegister/shareholderRegisterDomain";
import { findShareTypeName, ShareType, ShareTypeCode } from "@/common/share";
import { Actor, actorsApi, ActorType, SelectedActor } from "@/types/SelectActors";
import { partition } from "lodash";
import { InvitationRecipient } from "./SendInvitationDialogStore";
import { toShareholderReference } from "@/utils/UrnReference";
import { DCEvent } from "@/events/eventsDomain";
import {
  ExportFormat,
  ExportShareholderData,
} from "@/shareholderRegister/shareholders/ExportShareholdersDomain";
import { ExportTransactionsData } from "@/shareholderRegister/transactions/exportTransactionsDomain";
import {
  ShareholderFollowUp,
  ShareholderUpdatedNameFollowUp,
  ShareholderUpdatedNameFollowUpItem,
} from "@/portfolio/followUp/shareholdersFollowUpDomain";
import { IssueOfNewSharesTransactionModel } from "@/shareholderRegister/transactions/issue/issueTransactionDomain";
import { TransactionStore } from "./TransactionStore";
import {
  ValidationError,
  validationErrorMessage,
  validationErrorMessageFrom,
} from "@/utils/Errors";
import { filterShareTypesWithShares } from "@/shareholderRegister/transactions/cancellationOfShares/cancellationOfSharesDomain";
import { withUnidentifiedLast } from "@/shareholderRegister/shareholders/shareholdersDomain";

export function excludedShareholdersFromActors(
  shareholders: { id: number; isCompany: boolean; companyNumber?: string }[],
): string[] {
  return shareholders.flatMap((shareholder) =>
    compact([
      toShareholderReference(shareholder.id),
      shareholder.isCompany ? shareholder.companyNumber : undefined,
    ]),
  );
}

export function excludeExistingShareholders<T extends { id: number }>(
  shareholders: T[],
  existingShareholderIds: number[],
): T[] {
  return shareholders.filter((shareholder) => !existingShareholderIds.includes(shareholder.id));
}

export function shouldShowInactiveShareholders(shareholders: Array<ExtendedShareholderDTO>) {
  return shareholders.some(
    (s) =>
      s.createdAt !== undefined &&
      diffDays(s.createdAt, Date.now()) < 2 &&
      (s.calculatedShares === undefined ||
        Object.keys(s.calculatedShares).length === 0 ||
        Object.values(s.calculatedShares).reduce((a, b) => a + b) === 0),
  );
}

export interface ShareholderDetailsState {
  isLoading?: boolean;
  shareholder?: ShareholderDetailsResponseDTO;
  allShareholders: ExtendedShareholderDTO[];
  filteredShareholders: ExtendedShareholderDTO[];
  shareTypes: ShareType[];
  currency: string;
  canLogOnWithBankId?: boolean;
  canLogOnWithPassword?: boolean;
  shareholderStatus?: ShareholderStatus;
  isDeleting?: boolean;
  isDownloadingCertificate?: boolean;
}

export const toShareholderDetailsState = ({
  store,
  allShareholders,
  filteredShareholders,
  shareTypes,
  isLoading,
  shareholderStatus,
  isDeleting,
}: {
  store: ShareholderDetailsStore;
  allShareholders: ExtendedShareholderDTO[];
  filteredShareholders: ExtendedShareholderDTO[];
  shareTypes: ShareType[];
  isLoading?: boolean;
  shareholderStatus: ShareholderStatus;
  isDeleting?: boolean;
}): ShareholderDetailsState => ({
  isLoading: !store.shareholderDetails || isLoading,
  shareholder: store.shareholderDetails,
  allShareholders,
  filteredShareholders,
  shareTypes,
  currency: store.shareholderDetails?.shareInformation.currency ?? "",
  canLogOnWithBankId: store.canLogOnWithBankId,
  canLogOnWithPassword: store.hasUsername && store.hasPassword,
  shareholderStatus,
  isDeleting,
});

export default class ShareholdersStore {
  renderOnlyShareholdersWithIncompleteData = false;
  renderOnlyActiveShareholders = false;
  isRunAutoimport = false;
  sortedBy: keyof ExtendedShareholderDTO | undefined;
  sortedByType: string | undefined = undefined;
  sortDirection: "asc" | "desc" = "desc";
  nameFilter: StringFieldModel;
  shareholderDateFilter: LocalDateFieldModel;
  shareholdersForEdit: ShareholderModel[] = [];
  addedShareholderId?: number;
  shareholders: ExtendedShareholderDTO[] = [];
  followUps: ShareholderFollowUp[] = [];
  shareTypes: ShareType[] = [];
  shareTypeCodes: string[] = [];
  calculatedSharesBySharetypeCode: {
    [shareTypeCode: string]: number;
  } = {};
  calculatedVotesBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  } = {};
  isLoading = false;
  isEditMode = false;
  shareholderDetailsStore: ShareholderDetailsStore;
  readonly downloadService: DownloadService;
  trankMap: Result<OwnershipGraph, TMessage> = pending();
  parsedExcelResponse: ParseExcelResponse | undefined = undefined;
  showActiveShareholders = true;
  importExcelConfig?: FileUploadConfig;

  shareholderUpdateResult?: Result;
  shareholderExcelResult?: UploadShareholdersExcelResponse;
  lastSuccessfulUpdate: Date | undefined;

  autoimportResult: Result<TMessage, TMessage> | undefined = undefined;
  orgNo?: string;
  shareholdersResult: Result = pending();
  uploadShareholdersResult?: Result;
  importShareholdersResult?: Result;
  deleteResult?: Result<TMessage, TMessage>;
  deleteShareholderResult?: Result;
  shareholderStatusFilter: ShareholderStatus = "current";

  hideAutoImportSectionResult?: Result<void, TMessage>;

  importShareholderIdShares: Array<ShareholderIdShares> = [];
  shareholdingImportModel?: ShareholdingPerDateModel;
  issueImportModel?: IssueOfNewSharesTransactionModel;
  shareTypeFilter: RadioFieldModel<ShareTypeFilter>;

  shareholderUpdatedNamefollowUpDialogModel?: OptionFieldModel<ShareholderUpdatedNameFollowUpItem>;
  isShareholderPortalEnabled?: boolean;
  canAutoCalculatePayment = false;
  canAutoCalculateSharePremium = false;
  shareholderDetailsTimestamp?: string;

  constructor(
    readonly history: History,
    readonly apiService: ApiService,
    readonly http: AxiosInstance,
    readonly messageStore: AlertMessageStore,
    readonly userStore: UserStore,
    readonly dialogStore: DialogStore,
    readonly transactionStore: TransactionStore,
  ) {
    this.nameFilter = new StringFieldModel();
    this.shareholderDateFilter = new LocalDateFieldModel({
      onChange: () => this.reloadShareholdersByDate(),
    });
    this.shareholderDetailsStore = new ShareholderDetailsStore();
    this.downloadService = new DownloadService(http);
    this.shareTypeFilter = this.initShareTypeFilter();
    makeAutoObservable(
      this,
      { http: false, history: false, apiService: false },
      { autoBind: true },
    );
  }

  getShareholderDetailsState(): ShareholderDetailsState {
    return toShareholderDetailsState({
      store: this.shareholderDetailsStore,
      allShareholders: this.shareholders,
      filteredShareholders: this.sortedShareholders,
      shareTypes: this.shareTypes,
      isLoading: this.isLoadingShareTypes,
      shareholderStatus: this.shareholderStatusFilter,
      isDeleting: isPending(this.deleteShareholderResult),
    });
  }

  get activeShareholdersForEdit(): ShareholderModel[] {
    return this.shareholdersForEdit.filter((s) =>
      hasShares(this.shareholders.find((ss) => ss.id === s.id)),
    );
  }

  get activeShareTypes(): ShareType[] {
    return this.shareTypes.filter((s) => !s.historical);
  }

  get isLoadingShareTypes(): boolean {
    return isPending(this.shareholdersResult) && this.shareTypes.length === 0;
  }

  get shareSumBySharetypeCode(): { [shareTypeCode in ShareTypeCode]?: number } {
    return this.calculatedSharesBySharetypeCode;
  }

  get shareTypesWithShares(): ShareType[] {
    return filterShareTypesWithShares(this.activeShareTypes, this.calculatedSharesBySharetypeCode);
  }

  get transactionLog() {
    return this.transactionStore.transactionLog;
  }

  get transactionsError() {
    return this.transactionStore.transactionsError;
  }

  get transactionLogResult() {
    return this.transactionStore.transactionLogResult;
  }

  get emptyLogDueToNorwegianCompanyCreatedCurrentYear() {
    const currentYear = new Date().getFullYear();
    const companyCreatedAt = when(this.userStore.companyInfo?.company.created, toDate);
    const company = this.userStore.companyInfo?.company;
    return (
      currentYear === companyCreatedAt?.getFullYear() &&
      isCountryCodeNorway(company?.country) &&
      this.transactionStore.emptyTransactionLog
    );
  }

  get renderAutoImportSection() {
    return this.hasAutoImportOnly && !this.userStore.companyInfo?.company.confirmedAutoImport;
  }

  get renderAutoImportSectionLink() {
    return this.hasAutoImportOnly && this.userStore.companyInfo?.company.confirmedAutoImport;
  }

  get onboardingDate(): string | undefined {
    if (!this.renderAutoImportSection) {
      return undefined;
    } else {
      return this.transactionLog[0].transactionDate;
    }
  }

  get hasImportTransaction() {
    return this.shareholdingImportModel !== undefined || this.issueImportModel !== undefined;
  }

  get isSaving() {
    return isPending(this.shareholderUpdateResult);
  }

  *hideAutoImportSection() {
    this.hideAutoImportSectionResult = pending();
    try {
      yield this.apiService.markAutoImportMessageAsRendered(this.userStore.orgNumber);
      this.hideAutoImportSectionResult = ok();
      yield this.userStore.updateCompanyInfo();
    } catch (e) {
      this.hideAutoImportSectionResult = failure({
        key: "transactions.only-autoimport.hide.error",
      });
    }
  }

  get isLoadingHideAutoImport() {
    return isPending(this.hideAutoImportSectionResult);
  }

  *showAutoImportSection() {
    try {
      yield this.apiService.markAutoImportMessageAsNotRendered(this.userStore.orgNumber);
      yield this.userStore.updateCompanyInfo();
    } catch (e) {
      logError(e);
    }
  }

  get currency(): string {
    return this.userStore.companyInfo?.company?.currency ?? CURRENCY_NOK;
  }

  get country(): string {
    return this.userStore.companyInfo?.company?.country ?? norway;
  }

  get showNationalId() {
    return this.userStore.companyInfo?.showNationalId;
  }

  get isImportBlocked() {
    return this.parsedExcelResponse === undefined || isPending(this.importShareholdersResult);
  }

  initImportShareholdersEdit() {
    const shareholders = this.parsedExcelResponse?.parsed?.map((p) => p.shareholder) ?? [];
    this.shareholdersForEdit = shareholders.map((s) => new ShareholderModel(s, false, true));
    this.shareholdersForEdit.forEach((s) => s.validate(this.shareholdersForEdit));
  }

  validateActiveShareholders() {
    validateAllShareholders(this.activeShareholdersForEdit);
  }

  validateShareholders(shareholdersForValidation: ShareholderModel[]) {
    validateAllShareholders(shareholdersForValidation);
  }

  get shareholdersForValidation(): ShareholderModel[] {
    return this.showActiveShareholders ? this.activeShareholdersForEdit : this.shareholdersForEdit;
  }

  addNewShareholderForEdit(data: ExtendedShareholderDTO) {
    this.addedShareholderId = data.id;
    this.shareholdersForEdit.push(new ShareholderModel(data, false, this.showNationalId));
    this.renderOnlyActiveShareholders = false;
    this.renderOnlyShareholdersWithIncompleteData = false;
  }

  *addPersonShareholder() {
    const shareholderId: number = this.isEditMode
      ? yield this.addNewShareholder(ShareholderType.person)
      : -this.shareholdersForEdit.length - 1;
    this.addNewShareholderForEdit({
      id: shareholderId,
      data: [],
      email: "",
      name: "",
      personInfo: emptyPersonInfo,
      isCompany: false,
      organizationNumber: this.userStore.orgNumber,
      address: { country: norway },
      calculatedShares: {},
      calculatedVotes: {},
    });
  }

  *addCompanyShareholder() {
    const shareholderId: number = this.isEditMode
      ? yield this.addNewShareholder(ShareholderType.company)
      : -this.shareholdersForEdit.length - 1;

    this.addNewShareholderForEdit({
      id: shareholderId!,
      contactInfo: emptyPersonInfo,
      isCompany: true,
      organizationNumber: this.userStore.orgNumber,
      address: { country: norway },
      calculatedShares: {},
      calculatedVotes: {},
      data: [],
      email: "",
      name: "",
    });
  }

  get shareholdersForEditing(): ShareholderModel[] {
    let shareholders = this.shareholdersForEdit;
    if (this.renderOnlyActiveShareholders) {
      shareholders = shareholders.filter((s) => s.hasShares);
    }
    if (this.renderOnlyShareholdersWithIncompleteData) {
      return shareholders.filter((m) => m.hasIncompleteData());
    } else {
      return shareholders;
    }
  }

  get isTotalOverview(): boolean {
    return this.shareTypeFilter.value === "overview";
  }

  get filteredShareTypes(): ShareType[] {
    return this.activeShareTypes.filter(
      (shareType) =>
        this.shareTypeFilter.value === "overview" ||
        this.shareTypeFilter.value === "all" ||
        this.shareTypeFilter.value === "votes" ||
        shareType.sharetypeId === this.shareTypeFilter.value.sharetypeId,
    );
  }

  get sortedShareholders() {
    return withUnidentifiedLast(
      getSortedShareholders(
        this.filteredShareholders,
        this.sortedBy,
        this.sortedByType,
        this.sortDirection,
      ),
    );
  }

  get filteredShareholders(): ExtendedShareholderDTO[] {
    const shareholders = filterShareholdersByStatus(
      this.shareholders,
      this.shareholderStatusFilter,
    );
    const nameFilter = this.nameFilter.inputValue.toLowerCase();
    return nameFilter
      ? shareholders.filter((item) => item.name.toLowerCase().includes(nameFilter))
      : shareholders;
  }

  get showEmptyStateForFilteredShareholders(): boolean {
    return (
      this.shareholderStatusFilter !== "all" &&
      this.hasShareholders &&
      this.sortedShareholders.length === 0
    );
  }

  descriptionMessage(t: TFunction): string {
    return t("shareholders.showActiveShareholders-description", {
      status: t(notFoundKeyForStatus[this.shareholderStatusFilter]).toLowerCase(),
    });
  }

  setSorting(
    name: keyof ExtendedShareholderDTO | undefined,
    shareTypeName: string | undefined,
    sortDirection: "asc" | "desc",
  ) {
    this.sortedBy = name;
    this.sortedByType = shareTypeName;
    this.sortDirection = sortDirection;
  }

  autoimport() {
    this.isRunAutoimport = !this.isRunAutoimport;
  }

  *runAutoimport() {
    try {
      this.autoimportResult = pending();
      yield this.apiService.runAutoimport(this.userStore.orgNumber);
      this.autoimportResult = ok({ key: "shareholders.autoimport.successRunningAutoimport" });
    } catch (e) {
      this.autoimportResult = failure({ key: "shareholders.autoimport.errorRunningAutoimport" });
    }
  }

  toggleRenderOnlyShareholdersWithIncompleteData() {
    this.renderOnlyShareholdersWithIncompleteData = !this.renderOnlyShareholdersWithIncompleteData;
  }

  toggleRenderOnlyActiveShareholders() {
    this.renderOnlyActiveShareholders = !this.renderOnlyActiveShareholders;
  }

  get shareSumByType(): { [shareType: string]: number } {
    return getShareSumByType(this.shareholders);
  }

  openEditAllMode() {
    this.setEditMode(true);
    this.clearMessages();
    this.shareholderStatusFilter = "all";
    this.renderOnlyShareholdersWithIncompleteData = false;
    this.renderOnlyActiveShareholders = false;
    this.isRunAutoimport = false;
  }

  openEditIncompleteShareholders() {
    this.openEditAllMode();
    this.renderOnlyShareholdersWithIncompleteData = true;
    this.renderOnlyActiveShareholders = true;
  }

  resetOnCompanyChange() {
    this.setEditMode(false);
  }

  clearFilters() {
    this.renderOnlyShareholdersWithIncompleteData = false;
    this.renderOnlyActiveShareholders = false;
  }

  openAddNewShareClassDialog(
    locale: Language,
    http: AxiosInstance,
    onNewShareTypeCreated: (newShareType: ShareType) => void,
  ) {
    this.dialogStore.openAddNewShareClassDialog({
      shareholderStore: this,
      locale: locale,
      http: http,
      onFinish: (newShareType) => {
        this.shareTypes.push(newShareType);
        onNewShareTypeCreated(newShareType);
      },
    });
  }

  openActorSelectorDialog({
    title,
    excludeActors,
    onSubmit,
    isMultiSelect,
    tabs,
  }: {
    title: TMessage;
    excludeActors: string[];
    onSubmit: (result: SelectedActor[], onsuccess: () => void, onError: () => void) => void;
    isMultiSelect?: boolean;
    tabs?: ActorType[];
  }) {
    this.dialogStore.openActorSelectorDialog({
      title,
      organisationNumber: this.userStore.orgNumber,
      tabs: tabs ?? [ActorType.SHAREHOLDER, ActorType.BOARD_MEMBER, ActorType.CEO],
      excludeActors,
      onLoad: this.loadActors,
      onSubmit,
      isMultiSelect,
    });
  }

  get excludeAllShareholdersForEditAsActors() {
    return excludedShareholdersFromActors(this.shareholdersForEdit);
  }

  get excludeAllShareholdersAsActors() {
    return excludedShareholdersFromActors(this.shareholders);
  }

  loadActors(
    type: ActorType,
    onSuccess: (result: Actor[]) => DCEvent[],
    onFailure: () => DCEvent[],
  ) {
    switch (type) {
      case ActorType.SHAREHOLDER:
        this.dialogStore.dispatch([
          actorsApi.getShareholders({ orgNo: this.userStore.orgNumber, onSuccess, onFailure }),
        ]);
        break;
      case ActorType.ADMINISTERED_COMPANY:
      case ActorType.BOARD_MEMBER:
      case ActorType.CEO:
        this.dialogStore.dispatch([
          actorsApi.getCompanyRoles({
            orgNo: this.userStore.orgNumber,
            type,
            onSuccess,
            onFailure,
          }),
        ]);
        break;
      default:
        onSuccess([]);
    }
  }

  *addShareholdersFromActors(
    actors: SelectedActor[],
    onSuccess: (shareholders: ExtendedShareholderDTO[]) => void,
    onError: () => void,
  ) {
    try {
      const result: ExtendedShareholderDTO[] = yield this.apiService.addShareholdersFromActors(
        this.userStore.orgNumber,
        actors,
      );
      onSuccess(result);
    } catch (e) {
      onError();
    }
  }

  openEditShareholderDialog({
    locale,
    shareholderId,
    onSuccess,
  }: {
    locale: Language;
    onSuccess: (shareholder?: ExtendedShareholderDTO) => void;
    shareholderId?: number;
  }) {
    const orgNumber = this.userStore.orgNumber;
    this.dialogStore.openEditShareholderDialog({
      orgNumber,
      locale,
      shareholderId,
      onSuccess: onSuccess,
      loadShareholders: () => this.apiService.fetchShareholdersAsActors(orgNumber),
    });
  }

  navigateToCreateShareholdingPerDateTransactionType() {
    navigateToNewTransaction(
      this.userStore.orgNumber,
      this.history,
      TransactionType.SHAREHOLDING_PER_DATE,
    );
  }

  navigateToIncorporationTransactionType() {
    navigateToNewTransaction(this.userStore.orgNumber, this.history, TransactionType.INCORPORATION);
  }

  navigateToPaymentWizard() {
    this.userStore.openPaymentDialog();
  }

  openExportShareholdersDialog(selectedDate = this.shareholderDateFilter.validatedValue) {
    this.dialogStore.openExportShareholdersDialog({
      onSubmit: this.handleSubmitExport,
      selectedContext: this.userStore.orgNumber,
      selectedDate,
    });
  }

  openExportTransactionsDialog(initialLanguage: Language) {
    this.dialogStore.openExportTransactionsDialog({
      onSubmit: this.exportTransactions,
      initialLanguage,
    });
  }

  reloadShareholdersByDate() {
    const date = this.shareholderDateFilter.validatedValue;
    if (date) {
      this.setShareholderStatusFilter("current");
      this.loadShareholdersForDate(this.userStore.orgNumber, atTime(date, "23:59").toISOString(), {
        keepFilters: true,
      });
    } else {
      this.setShareholderStatusFilter("current");
      this.loadAllShareholders();
    }
  }

  clearDateFilterAndLoadCurrent() {
    this.shareholderDetailsTimestamp = undefined;
    this.shareholderDateFilter.setAndCommitInputValue(undefined);
  }

  get isPerDateSelected() {
    return Boolean(this.shareholderDateFilter.validatedValue);
  }

  *handleSubmitExport(data: ExportShareholderData, onSuccess: () => void, onFailure: () => void) {
    if (data.exportFormat === ExportFormat.HTML) {
      window.open(
        `/api/shareholder/export/${this.userStore.orgNumber}?language=${data.language}&format=${data.format}&exportFormat=${data.exportFormat}&date=${data.date}`,
      );
      onSuccess();
    } else {
      yield this.exportShareholders(data, onSuccess, onFailure);
    }
  }

  // same as `shareSumByType` but without own company shares
  get shareSumWithoutCompany(): { [shareType: string]: number } {
    return getShareSumWithoutCompany(
      this.userStore.orgNumber,
      this.shareholders,
      this.shareSumByType,
    );
  }

  get shareholdersById(): Record<number, ExtendedShareholderDTO | undefined> {
    return this.shareholders.reduce((byId, s) => ({ [s.id]: s, ...byId }), {});
  }

  get shareholderData() {
    return {
      shareholders: this.shareholders,
      shareTypes: this.shareTypes,
      shareTypeCodes: this.shareTypeCodes,
      shareSumByType: this.shareSumByType,
      shareSumWithoutCompany: this.shareSumWithoutCompany,
      trankMap: this.trankMap,
    };
  }

  get isCurrentCompanySwedish(): boolean {
    return isSwedishOrgNo(this.userStore.orgNumber);
  }

  get hasMissingShareholders() {
    return (
      this.userStore.companyInfo &&
      this.userStore.companyInfo?.numberOfSharesWithMissingOwner > 0 &&
      this.hasAutoImportOnly
    );
  }

  get hasAutoImportOnly() {
    return (
      this.transactionLog.length === 1 &&
      this.transactionLog[0].transactionType === TransactionType.AUTOIMPORT
    );
  }

  storeName(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.shareholderName, s.nameModel);
  }

  storeContactInfoName(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.contactInfoName, s.contactInfoNameModel);
  }

  storeContactInfoNationalId(s: ShareholderModel) {
    this.storeShareholderField(
      s,
      ShareholderField.contactInfoNationalId,
      s.contactInfoNationalIdModel,
    );
  }

  storeEmail(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.email, s.emailModel);
  }

  *storeGender(s: ShareholderModel) {
    if (this.isEditMode) {
      yield this.postShareholderField(
        s.shareholderId,
        ShareholderField.gender,
        s.genderModel.value ? s.genderModel.value : "",
      );
    }
    s.validate(this.shareholdersForValidation);
  }

  *storeIsEmployee(s: ShareholderModel) {
    if (this.isEditMode) {
      yield this.postShareholderField(
        s.shareholderId,
        ShareholderField.isEmployee,
        s.isEmployeeModel.value === null ? "" : s.isEmployeeModel.value.toString(),
      );
    }
    s.validate(this.shareholdersForValidation);
  }

  storePhone(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.phone, s.phoneModel);
  }

  *storeNationalId(s: ShareholderModel) {
    const rejection: ValidationError | undefined = yield this.storeShareholderField(
      s,
      ShareholderField.nationalId,
      s.nationalIdModel,
    );
    if (rejection !== undefined) {
      s.nationalIdModel.setError(validationErrorMessage[rejection]);
    }
  }

  storeNationality(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.nationality, s.nationalityModel);
  }

  storeCompanyNumber(s: ShareholderModel) {
    s.companyNumberModel.setError(undefined);
    if (s.validateDuplicateCompanyNumber(this.shareholdersForValidation)) {
      this.storeShareholderField(s, ShareholderField.companyNumber, s.companyNumberModel);
    }
    this.updateCompanyDataFromSuggestedCompany(s);
  }

  *updateCompanyDataFromSuggestedCompany(s: ShareholderModel) {
    try {
      if (s.companyNumberModel.validate() && s.postalCodeModel.isEmpty && s.cityModel.isEmpty) {
        const companies: SuggestedCompany[] = yield this.apiService.getCompanies(
          s.companyNumberModel.validatedValue,
        );
        if (companies.length === 1) {
          const company = companies[0];
          if (company.zip && company.city) {
            s.postalCodeModel.setAndCommitInputValue(company.zip);
            s.cityModel.setAndCommitInputValue(company.city);
            this.storePostalCode(s);
            this.storeCity(s);
          }
        }
      }
    } catch (e) {
      // no action
    }
  }

  storeUtlNumber(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.utlNumber, s.utlNumberModel);
  }

  storeLockupEndDate(s: ShareholderModel): void {
    this.storeShareholderField(s, ShareholderField.lockupEndDate, s.lockupEndDateModel);
  }

  storeLoanAmount(s: ShareholderModel): void {
    this.storeShareholderField(s, ShareholderField.loanAmount, s.loanAmountModel);
  }

  storeIssuedShareholderCertificate(s: ShareholderModel, checked: boolean): void {
    s.issuedShareholderCertificate.setInputValue(String(checked));
    s.issuedShareholderCertificate.commit();
    this.storeShareholderField(
      s,
      ShareholderField.issuedShareholderCertificate,
      s.issuedShareholderCertificate,
    );
  }

  storeAddress(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.address, s.addressModel);
  }

  storePostalCode(s: ShareholderModel) {
    if (s.postalCodeModel.validate()) {
      if (s.countryModel.inputValue === norway) {
        const city = lookupNorwegianCity(s.postalCodeModel.inputValue);
        if (city !== undefined) {
          s.cityModel.setAndCommitInputValue(city);
          this.storeCity(s);
        }
      }
      this.storeShareholderField(s, ShareholderField.postalCode, s.postalCodeModel);
    }
  }

  storeCity(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.city, s.cityModel);
  }

  storeCountry(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.country, s.countryModel);
  }

  storeRemark(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.remark, s.remarkModel);
  }

  *storeShareholderField(
    s: ShareholderModel,
    field: ShareholderField,
    model: StringFieldModel | LocalDateFieldModel | NumberFieldModel,
  ) {
    this.clearMessages();
    if (model.validate() && model.isChanged) {
      let value: string;
      if (model instanceof NumberFieldModel) {
        value = model.parsedValue?.toString() ?? "";
      } else {
        value = model.validatedValue?.toString() ?? "";
      }
      model.rebase();
      const rejection: ValidationError | undefined = this.isEditMode
        ? yield this.postShareholderField(s.shareholderId, field, value)
        : undefined;
      s.validate(this.shareholdersForValidation);
      return rejection;
    }
    return undefined;
  }

  *deleteShareholderForEdit(shareholderId: number) {
    const removeShareholderForEdit = () => {
      const index = this.shareholdersForEdit.findIndex((s) => s.shareholderId === shareholderId);
      if (index > -1) {
        this.shareholdersForEdit.splice(index, 1);
      }
    };

    if (this.isEditMode) {
      yield this.deleteShareholder(shareholderId, removeShareholderForEdit);
    } else removeShareholderForEdit();
  }

  *deleteShareholder(shareholderId: number, onSuccess?: () => void) {
    this.clearMessages();
    try {
      this.deleteShareholderResult = pending();
      yield this.apiService.deleteShareholderHard(shareholderId);
      onSuccess?.();
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.deleting" });
    } finally {
      this.deleteShareholderResult = undefined;
    }
  }

  *postShareholderField(shareholderId: number, field: ShareholderField, value: string) {
    this.shareholderUpdateResult = pending();
    try {
      const response: UpdateShareholderFieldResponse = yield this.apiService.storeShareholderField(
        shareholderId,
        field,
        value,
      );
      this.shareholderUpdateResult = ok();
      if (response.success) {
        this.lastSuccessfulUpdate = new Date();
        return undefined;
      }
      this.messageStore.showError({
        key: "shareholder.updateError",
        params: { error: validationErrorMessage[response.validationError] },
      });
      return response.validationError;
    } catch (e) {
      this.shareholderUpdateResult = failure();
      this.messageStore.showError({ key: "shareholders.error.storing" });
    } finally {
      this.setLoading(false);
    }
  }

  private *addNewShareholder(shareholderType: ShareholderType) {
    try {
      const newShareholder: NewShareholderResponse = yield this.apiService.addNewShareholder(
        this.userStore.orgNumber,
        shareholderType,
      );
      return newShareholder.shareholderId;
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.storing" });
      throw e;
    } finally {
      this.setLoading(false);
    }
  }

  private buildFileFormData(files: File[]): FormData {
    const formData = new FormData();
    for (const file of files) formData.append("file", file);
    return formData;
  }

  *uploadShareholderExcel(files: File[] | null) {
    if (!files) return false;
    const formData = this.buildFileFormData(files);
    try {
      this.uploadShareholdersResult = pending();
      this.parsedExcelResponse = yield this.apiService.parseShareholdersExcel(
        this.userStore.orgNumber,
        formData,
      );
      this.initImportShareholdersEdit();
      this.uploadShareholdersResult = ok();
    } catch (e) {
      this.uploadShareholdersResult = failure();
      this.messageStore.showError(importShareholdersErrorMessage(e));
    } finally {
      this.setLoading(false);
    }
  }

  *uploadShareholderExcelForUpdate(files: File[] | null) {
    if (!files) return false;
    const formData = this.buildFileFormData(files);
    try {
      this.shareholderUpdateResult = pending();
      this.shareholderExcelResult = undefined;
      const response: UploadShareholdersExcelResponse =
        yield this.apiService.uploadShareholdersExcel(this.userStore.orgNumber, formData);
      this.shareholderUpdateResult = ok();
      this.shareholderExcelResult = response;

      yield this.loadAllShareholders();
    } catch (e) {
      this.shareholderUpdateResult = failure();
      this.messageStore.showError(uploadShareholdersErrorMessage(e));
    } finally {
      this.setLoading(false);
    }
  }

  closeShareholderExcelResult() {
    this.shareholderExcelResult = undefined;
  }

  initImport() {
    this.uploadShareholdersResult = undefined;
    this.importShareholdersResult = undefined;
    this.parsedExcelResponse = undefined;
    this.shareholdingImportModel = undefined;
    this.issueImportModel = undefined;
    this.renderOnlyActiveShareholders = false;
    this.renderOnlyShareholdersWithIncompleteData = false;
  }

  *importShareholders() {
    this.shareholdersForEdit.forEach((s) => s.validate(this.shareholdersForEdit));

    const shareholders = this.shareholdersForEdit.map((s) =>
      s.prepareImportData(this.parsedExcelResponse?.parsed ?? []),
    );

    try {
      this.importShareholdersResult = pending();
      this.importShareholderIdShares = yield this.apiService.importShareholders(
        this.userStore.orgNumber,
        shareholders,
      );
      this.importShareholdersResult = ok();

      const isImportingShares = this.importShareholderIdShares.some(
        (s) => s.shares !== undefined && s.shares.some((x) => (x.numberOfShares ?? 0) > 0),
      );

      if (!isImportingShares) {
        navigateToShareholdersList(this.userStore.orgNumber, this.history);
        this.messageStore.showSuccess(
          {
            key: "shareholderImport.success",
            params: { count: shareholders.length },
          },
          { autoHide: false },
        );
      } else if (this.transactionLog.length === 0) {
        yield this.loadAllShareholders();

        this.shareholdingImportModel = new ShareholdingPerDateModel(
          this.transactionStore,
          this.userStore,
          this,
        );
        this.shareholdingImportModel.initFromImport(
          this.importShareholderIdShares,
          this.importEntryDateByShareholderId,
        );
      } else {
        yield this.loadAllShareholders();

        this.issueImportModel = new IssueOfNewSharesTransactionModel(
          this,
          this.userStore,
          this.transactionStore,
          this.importShareholderIdShares,
        );
      }
    } catch (e) {
      this.importShareholdersResult = failure();
      this.messageStore.showError(validationErrorMessageFrom(e) ?? { key: "general-error" });
    } finally {
      this.setLoading(false);
    }
  }

  *loadAllShareholders() {
    yield this.loadShareholdersForOrgNumber(this.userStore.orgNumber);
  }

  *loadShareholdersForOrgNumber(orgNumber: string) {
    const prevOrgNo = this.orgNo;
    this.orgNo = orgNumber;
    if (orgNumber !== prevOrgNo) {
      this.shareTypes = [];
    }
    try {
      this.shareholdersResult = pending();
      this.setLoading(true);
      const shareholdersInfo: ShareholdersInfo =
        yield this.apiService.fetchShareholdersAll(orgNumber);
      this.setShareholders(shareholdersInfo, { keepFilters: orgNumber === prevOrgNo });
      this.setShareholdersForEdit(shareholdersInfo.shareholders);
      this.shareholdersResult = ok();
    } catch (e) {
      this.shareholdersResult = failure();
      this.messageStore.showError({ key: "shareholders.error.load" });
    } finally {
      this.setLoading(false);
    }
  }

  *loadShareholdersForDate(orgNumber: string, date: string, options?: { keepFilters?: boolean }) {
    this.setLoading(true);
    try {
      this.shareholdersResult = pending();
      const shareholdersInfo: ShareholdersInfo = yield this.apiService.fetchShareholdersAll(
        orgNumber,
        date,
      );
      this.setShareholders(shareholdersInfo, {
        keepFilters: options?.keepFilters ?? false,
      });
      this.shareholdersResult = ok();
    } catch (e) {
      this.shareholdersResult = failure();
    }
    this.setLoading(false);
  }

  setShareholders(shareholdersInfo: ShareholdersInfo, options?: { keepFilters?: boolean }) {
    this.shareholders = shareholdersInfo.shareholders;
    this.shareTypes = shareholdersInfo.shareTypes;
    this.shareTypeCodes = shareholdersInfo.shareTypeCodes;
    this.calculatedSharesBySharetypeCode = shareholdersInfo.calculatedSharesBySharetypeCode;
    this.calculatedVotesBySharetypeCode = shareholdersInfo.calculatedVotesBySharetypeCode;
    this.followUps = shareholdersInfo.followUps;
    this.showActiveShareholders = !shouldShowInactiveShareholders(this.shareholders);
    if (!options?.keepFilters) {
      this.shareholderStatusFilter = "current";
    }
    this.importExcelConfig = shareholdersInfo.importExcelConfig;
    this.isShareholderPortalEnabled = shareholdersInfo.isShareholderPortalEnabled;
    this.canAutoCalculatePayment = shareholdersInfo.canAutoCalculatePayment ?? false;
    this.canAutoCalculateSharePremium = shareholdersInfo.canAutoCalculateSharePremium ?? false;
    this.shareTypeFilter = this.initShareTypeFilter();
  }

  private initShareTypeFilter() {
    return new RadioFieldModel<ShareTypeFilter>({
      idFn: (option) => (typeof option === "string" ? option : option.shareTypeCode),
      labelFn: () => ({ translated: "" }),
      options: ["overview", "all", "votes", ...this.activeShareTypes],
      value: "overview",
    });
  }

  setShareholdersForEdit(shareholders: ExtendedShareholderDTO[]) {
    this.shareholdersForEdit = shareholders
      .filter((s) => s.isUnidentified !== true)
      .map(
        (shareholder) =>
          new ShareholderModel(shareholder, hasShares(shareholder), this.showNationalId),
      );
    this.addedShareholderId = undefined;
  }

  get importEntryDateByShareholderId() {
    return new Map(this.importShareholderIdShares.map((s) => [s.shareholderId, s.entryDate]));
  }

  get hasShareholders(): boolean {
    return this.shareholders.length > 0;
  }

  get activeShareholders(): ExtendedShareholderDTO[] {
    return this.shareholders.filter((s) => hasShares(s));
  }

  *exportShareholders(data: ExportShareholderData, onSuccess: () => void, onFailure: () => void) {
    try {
      const orgNumbers = data.organizationnumbers ?? [this.userStore.orgNumber];
      const res: AxiosResponse = yield this.downloadService.exportShareholdersBatch(
        orgNumbers,
        data.language,
        data.format,
        data.date,
        data.exportFormat,
      );
      downloadFile(res.data, getResponseFilename(res));
      onSuccess();
    } catch (e) {
      onFailure();
      this.messageStore.showError({ key: "shareholders.error.export" });
    }
  }

  *exportTransactions(data: ExportTransactionsData, onSuccess: () => void, onFailure: () => void) {
    try {
      const res: AxiosResponse = yield this.downloadService.exportTransactions(
        this.userStore.orgNumber,
        data.language,
      );
      downloadFile(res.data, getResponseFilename(res));
      onSuccess();
    } catch (e) {
      onFailure();
      this.messageStore.showError({ key: "transactions.error.export" });
    }
  }

  *downloadTransactionsTemplate(language: Language) {
    try {
      const res: AxiosResponse = yield this.downloadService.downloadTransactionsTemplate(
        this.userStore.orgNumber,
        language,
      );
      downloadFile(res.data, getResponseFilename(res));
    } catch (e) {
      this.messageStore.showError({ key: "transactions.error.template" });
    }
  }

  *downloadShareholdersExcelFile() {
    const res: AxiosResponse = yield this.downloadService.exportShareholdersData(
      this.userStore.orgNumber,
    );
    downloadFile(res.data, getResponseFilename(res));
  }

  *downloadShareholderCertificate(shareholderId: string | number, locale: Language) {
    this.setLoading(true);
    try {
      const res: AxiosResponse = yield this.downloadService.downloadShareholderCertificate(
        shareholderId,
        locale,
      );
      downloadFile(res.data, getResponseFilename(res));
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.downloadingCertificate" });
    } finally {
      this.setLoading(false);
    }
  }

  getTransactionLog() {
    return this.transactionStore.getTransactionLog();
  }

  /* Can be used on page mount */
  clearMessages = (): void => {
    this.transactionStore.setTransactionsError(undefined);
    this.shareholderUpdateResult = undefined;
  };

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setEditMode(isEditMode: boolean) {
    this.isEditMode = isEditMode;
  }

  clearTrankMap() {
    this.trankMap = pending();
  }

  loadTrankMap(orgNumber: string) {
    this.trankMap = pending();
    this.dialogStore.dispatch([
      ownershipApi.getOwnershipGraph({
        id: orgNumber,
        type: OwnershipGraphType.owner,
        threshold: 0.01,
        maxnum: 0,
        onSuccess: (res) => {
          this.trankMap = ok(res.data);
          return [];
        },
        onFailure: () => {
          this.trankMap = failure({ key: "dashboard.shareholder-map.error" });
          return [];
        },
      }),
    ]);
  }

  showCancelAutoimportButton(): boolean {
    return this.autoimportResult === undefined || isPending(this.autoimportResult);
  }

  setShareholderStatusFilter(value: ShareholderStatus) {
    this.shareholderStatusFilter = value;
  }

  *reloadShareholderDetails() {
    if (this.shareholderDetailsStore.shareholderDetails) {
      yield this.getShareholderDetails(
        this.shareholderDetailsStore.shareholderDetails.shareholderId,
      );
    }
  }

  *getShareholderDetails(id: number, ...timestampArg: [string?]) {
    try {
      const timestamp =
        timestampArg.length > 0 ? timestampArg[0] : this.shareholderDetailsTimestamp;
      this.shareholderDetailsTimestamp = timestamp;
      this.shareholderDetailsStore.setLoading(true);
      const shareholderDetails: ShareholderDetailsResponseDTO =
        yield this.apiService.fetchShareholderById(id, timestamp);
      this.shareholderDetailsStore.shareholderDetails = shareholderDetails;
      this.shareholderDetailsStore.shareholderPledgeForm.setIsPledged(shareholderDetails.isPledged);
      this.shareholderDetailsStore.shareholderPledgeForm.setPledgeComment(
        shareholderDetails.pledgeComment,
      );
      this.shareholderDetailsStore.shareholderRemarksForm.setShareholderName(
        shareholderDetails.shareholderName,
      );
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.load" });
    } finally {
      this.shareholderDetailsStore.setLoading(false);
    }
  }

  *updateShareholderPledge(id: number, form: ShareholderPledgeForm) {
    form.setMode("saving");
    try {
      yield this.apiService.updateShareholderPledge(id, form);
      form.setMode("view");
      form.resetError();
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.save" });
      form.setMode("edit");
    }
  }

  *updateShareholderRemarks(id: number, form: ShareholderRemarksForm) {
    const currentMode = form.mode;
    form.setMode("saving");
    try {
      yield this.apiService.updateShareholderRemark(id, form);
      form.setMode("hide");
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.save" });
      form.setMode(currentMode);
    }
  }

  *deleteRepresentative(id: number) {
    if (!this.shareholderDetailsStore.shareholderDetails) return;
    const { shareholderId } = this.shareholderDetailsStore.shareholderDetails;
    try {
      yield this.apiService.deleteRepresentatives(shareholderId, [id]);
      yield this.getShareholderDetails(shareholderId);
      this.messageStore.showGeneralSuccess();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  isTransactionLogChangedInLastDay() {
    return this.transactionStore.isTransactionLogChangedInLastDay();
  }

  openShareholderUpdatedNameFollowUpDialog(followUp: ShareholderUpdatedNameFollowUp) {
    this.shareholderUpdatedNamefollowUpDialogModel = new OptionFieldModel({
      selectedIds: followUp.shareholders.map((s) => s.companyNumber),
      options: followUp.shareholders,
      idFn: (value) => value.companyNumber,
      labelFn: (value) => ({ translated: value.companyName }),
    });
  }

  closeShareholderUpdatedNameFollowUpDialog() {
    this.shareholderUpdatedNamefollowUpDialogModel = undefined;
  }

  *submitShareholderUpdatedNameFollowUpDialog() {
    const orgNos = this.shareholderUpdatedNamefollowUpDialogModel?.selectedIds;
    if (!orgNos) return;

    try {
      yield this.apiService.updateShareholderNames(this.orgNo!, orgNos);

      this.closeShareholderUpdatedNameFollowUpDialog();
      this.messageStore.showSuccess({
        key: "followup.shareholders.dialog-success",
        params: { count: orgNos.size },
      });

      this.loadAllShareholders();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }
}

export class ShareholderDetailsStore {
  shareholderDetails: ShareholderDetailsResponseDTO | undefined = undefined;
  shareholderPledgeForm: ShareholderPledgeForm;
  shareholderRemarksForm: ShareholderRemarksForm;

  isLoading = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.shareholderPledgeForm = new ShareholderPledgeForm();
    this.shareholderRemarksForm = new ShareholderRemarksForm();
  }

  get hasUsername() {
    return this.shareholderDetails?.usernames && this.shareholderDetails.usernames.length > 0;
  }

  get hasPassword() {
    return this.shareholderDetails?.usernames.some((d) => d.hasPassword);
  }

  get transactionLog(): Transaction[] {
    return this.shareholderDetails?.transactionLog ?? [];
  }

  get isCompany() {
    return this.shareholderDetails?.isCompany ?? false;
  }

  get personOrContactInfo() {
    const info = this.isCompany
      ? this.shareholderDetails?.contactInfo
      : this.shareholderDetails?.personInfo;

    if (!info) return undefined;

    return {
      shareholderName: removeExtraSpaces(
        `${info.firstName} ${info.middleName ?? ""} ${info.lastName}`,
      ),
      phoneNumber: formatPhoneNumber(info.phoneNumber),
      email: info.email ?? "",
      gender: info.gender,
    };
  }

  get shareholderOrCompanyName() {
    return this.isCompany
      ? (this.shareholderDetails?.companyName ?? "")
      : (this.personOrContactInfo?.shareholderName ?? "");
  }

  get iban() {
    return this.shareholderDetails?.iban ?? "";
  }

  get bic() {
    return this.shareholderDetails?.bic ?? "";
  }

  get isEmployee() {
    return this.shareholderDetails?.isEmployee;
  }

  get nationality() {
    return this.shareholderDetails?.nationality ?? "";
  }

  get issuedShareholderCertificate() {
    return this.shareholderDetails?.issuedShareholderCertificate ? "yes" : "no";
  }

  get lastLogin() {
    return this.shareholderDetails?.lastLogin;
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  resetShareholder() {
    this.shareholderDetails = undefined;
    this.shareholderPledgeForm.resetForm();
  }

  get personInfoOrContactInfo(): PersonInfo | undefined {
    if (this.shareholderDetails?.isCompany) {
      return this.shareholderDetails?.contactInfo;
    } else {
      return this.shareholderDetails?.personInfo;
    }
  }

  get supportedBankIdCountry(): boolean {
    const country = this.shareholderDetails?.shareholderAddress?.country;
    return isCountryCodeSweden(country) || isCountryCodeNorway(country);
  }

  get canLogOnWithBankId(): boolean {
    return (
      this.supportedBankIdCountry && isNotBlank(this.personInfoOrContactInfo?.socialSecurityNumber)
    );
  }
}

export function toShareholderRecipients(
  shareholders: ExtendedShareholderDTO[],
): InvitationRecipient[] {
  const [withEmail, withoutEmail] = partition(shareholders.filter(canBeInvited), (s) => s.email);
  return [...withoutEmail, ...withEmail].map((s) => ({
    shareholderId: s.id,
    name: s.name,
    email: s.email ?? "",
    lastLogin: s.lastLogin,
  }));
}

export function hasShares(s: ExtendedShareholderDTO | undefined): boolean {
  return s !== undefined && Object.values(s.calculatedShares).some((c) => c > 0);
}

export function isOptionHolder(s: ExtendedShareholderDTO | undefined): boolean {
  return s?.isOptionHolder ?? false;
}

const notFoundKeyForStatus: Record<ShareholderStatus, TMessageKey> = {
  all: "shareholders.description.not-found-current",
  current: "shareholders.description.not-found-current",
  former: "shareholders.description.not-found-former",
  "option-holders": "shareholders.description.not-found-option-holders",
};

// The portal is for people who currently hold something: shares or options.
export function canBeInvited(s: ExtendedShareholderDTO | undefined): boolean {
  return hasShares(s) || isOptionHolder(s);
}

export function filterActiveShareholders(
  shareholders: ExtendedShareholderDTO[],
  showActiveShareholders = true,
): ExtendedShareholderDTO[] {
  return shareholders.filter((s) => {
    return !showActiveShareholders || hasShares(s);
  });
}

export function filterShareholdersByStatus(
  shareholders: ExtendedShareholderDTO[],
  status: ShareholderStatus,
): ExtendedShareholderDTO[] {
  switch (status) {
    case "all":
      return shareholders;
    case "current":
      return shareholders.filter((s) => hasShares(s));
    case "former":
      return shareholders.filter((s) => !hasShares(s) && !isOptionHolder(s));
    case "option-holders":
      return shareholders.filter(isOptionHolder);
  }
}

export function getSortedShareholders(
  filteredShareholders: ExtendedShareholderDTO[],
  sortedBy: keyof ExtendedShareholderDTO | undefined = undefined,
  sortedByType: string | undefined = undefined,
  sortDirection: "asc" | "desc" = "desc",
): ExtendedShareholderDTO[] {
  if (sortedBy) {
    return orderBy(
      filteredShareholders.map((i) => ({
        ...i,
        sortField: sortedBy === "name" ? i[sortedBy].toLowerCase() : "",
      })),
      sortedBy === "name" ? "sortField" : [sortedBy],
      [sortDirection],
    );
  }
  if (sortedByType === "totalshares" || sortedByType === "percentageShares") {
    return orderBy(
      filteredShareholders,
      [(s) => totalNumberOfSharesOfShareholder(s)],
      [sortDirection],
    );
  }
  if (sortedByType === "totalVotes" || sortedByType === "percentageVotes") {
    return orderBy(
      filteredShareholders,
      [(s) => totalNumberOfVotesForShareholder(s)],
      [sortDirection],
    );
  }
  if (sortedByType) {
    const sortedByTypeOriginal = sortedByType?.replace("-percentage", "");
    return orderBy(
      filteredShareholders,
      [(s) => s.calculatedShares[sortedByTypeOriginal as ShareTypeCode] ?? 0],
      [sortDirection],
    );
  }
  return orderBy(
    filteredShareholders,
    [(s) => totalNumberOfVotesForShareholder(s)],
    [sortDirection],
  );
}

export const emptyPersonInfo: PersonInfo = {
  phoneNumber: { value: "", countryCode: "" },
  firstName: "",
  lastName: "",
};

export function showLiquidationInfo(transactionLog: Transaction[]): boolean {
  return transactionLog.some((t) => t.transactionType === TransactionType.LIQUIDATION);
}

export type ShareTypeFilter = "overview" | "all" | "votes" | ShareType;

export function shareTypeFilterLabel(filter: ShareTypeFilter, language: Language): TMessage {
  switch (filter) {
    case "overview":
      return { key: "total-overview" };
    case "all":
      return { key: "shareholders.all-shares" };
    case "votes":
      return { key: "shareholders.votes-filter" };
    default:
      return { translated: findShareTypeName(filter, language) };
  }
}
